'use client'

import { Button } from '@/components/ui/button'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
  navigationMenuTriggerStyle,
} from '@/components/ui/navigation-menu'
import { useDictionary } from '@/contexts/DictionaryContext'
import { getLanguageSwitchedUrl, type Language } from '@/lib/blog-language-mapping'
import { cn } from '@/lib/utils'
import { AnimatePresence } from 'framer-motion'
import { ChevronDown, Menu } from 'lucide-react'
import Image from 'next/image'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { useEffect, useState } from 'react'
import MobileNavbar from './MobileNavbar'

type NavigationItem = {
  name: string
  href: string
  children?: NavigationItem[]
}

export default function Navbar() {
  const { dictionary, locale } = useDictionary()
  const pathname = usePathname()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)

  // Define navigation with translations
  const navigation: NavigationItem[] = [
    { name: dictionary.common.navigation.home, href: `/${locale}` },
    { name: dictionary.common.navigation.about, href: `/${locale}/about-us` },
    {
      name: dictionary.common.navigation.services,
      href: '#',
      children: [
        { name: dictionary.home.services.sections.industrial.title, href: `/${locale}/services/industrial-automation` },
        { name: dictionary.home.services.sections.turbo.title, href: `/${locale}/services/turbo-machinery` },
        { name: dictionary.home.services.sections.digital.title, href: `/${locale}/services/digital-transformation` },
        { name: dictionary.home.services.sections.energy.title, href: `/${locale}/services/energy-management-systems` },
      ],
    },
    { name: dictionary.common.navigation.blog, href: `/${locale}/blog` },
    { name: dictionary.common.navigation.contact, href: `/${locale}/contact` },
  ]

  // Language options for the dropdown
  const languages = [
    { code: 'en', name: 'English' },
    { code: 'tr', name: 'Türkçe' },
  ]

  useEffect(() => {
    // Disable body scroll when mobile menu is open
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }

    return () => {
      document.body.style.overflow = ''
    }
  }, [mobileMenuOpen])

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 20) {
        setScrolled(true)
      } else {
        setScrolled(false)
      }
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  return (
    <header
      className={cn(
        'fixed top-0 z-40 w-full select-none transition-all duration-300 print:hidden backdrop-blur-xl',
        scrolled ? 'bg-black/80 shadow-lg' : 'bg-gradient-to-b from-black/80 to-transparent'
      )}
      role='banner'
    >
      <nav
        className='mx-auto flex max-w-7xl items-center justify-between p-4 lg:px-8'
        aria-label='Main navigation'
      >
        <div className='flex lg:flex-1'>
          <Link
            href={`/${locale}`}
            className='-m-1.5 p-1.5 transition-all hover:opacity-80'
            draggable='false'
            aria-label='Go to homepage'
          >
            <span className='sr-only'>{dictionary.common.logo.srOnly}</span>
            <Image
              className='h-9 w-auto select-none'
              src={'/assets/images/logo/logo.svg'}
              alt={dictionary.common.logo.altText}
              width={180}
              height={50}
              draggable='false'
              priority
            />
          </Link>
        </div>

        <div className='flex lg:hidden'>
          <Button
            variant={'ghost'}
            size='icon'
            type='button'
            className='rounded-full text-white hover:bg-white/10 hover:text-white'
            onClick={() => setMobileMenuOpen(true)}
            aria-expanded={mobileMenuOpen}
          >
            <span className='sr-only'>{dictionary.common.navigation.openMenu}</span>
            <Menu
              className='h-6 w-6'
              aria-hidden='true'
            />
          </Button>
        </div>

        <div className='hidden lg:flex lg:gap-x-2'>
          <NavigationMenu>
            <NavigationMenuList>
              {navigation.map(item => (
                <NavigationMenuItem key={item.name}>
                  {item.children ? (
                    <>
                      <NavigationMenuTrigger className='bg-transparent hover:bg-white/10 hover:text-white focus:bg-white/10'>{item.name}</NavigationMenuTrigger>
                      <NavigationMenuContent>
                        <ul className='grid w-[400px] gap-1 p-2 bg-black/90 backdrop-blur-xl'>
                          {item.children.map(subItem => (
                            <li key={subItem.name}>
                              <NavigationMenuLink asChild>
                                <Link
                                  href={subItem.href}
                                  className='block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-white/10 hover:text-white focus:bg-white/10'
                                >
                                  <div className='text-sm font-medium leading-none'>{subItem.name}</div>
                                </Link>
                              </NavigationMenuLink>
                            </li>
                          ))}
                        </ul>
                      </NavigationMenuContent>
                    </>
                  ) : (
                    <Link
                      href={item.href}
                      className={cn(
                        navigationMenuTriggerStyle(),
                        'bg-transparent hover:bg-white/10 hover:text-white focus:bg-white/10',
                        pathname === item.href && 'bg-white/10 text-white'
                      )}
                    >
                      {item.name}
                    </Link>
                  )}
                </NavigationMenuItem>
              ))}
            </NavigationMenuList>
          </NavigationMenu>
        </div>

        <div className='hidden lg:flex lg:flex-1 lg:justify-end'>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant='ghost'
                size='sm'
                className='gap-2 text-white hover:bg-white/10 hover:text-white'
              >
                <span>{locale === 'en' ? 'EN' : 'TR'}</span>
                <ChevronDown className='h-4 w-4 opacity-70' />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align='end'
              className='bg-black/90 backdrop-blur-xl border-white/10'
            >
              {languages.map(lang => (
                <DropdownMenuItem
                  key={lang.code}
                  asChild
                >
                  <Link
                    href={getLanguageSwitchedUrl(pathname || '', locale as Language, lang.code as Language)}
                    className={cn('flex items-center gap-2 text-white', locale === lang.code ? 'bg-white/10' : '')}
                  >
                    <span>{lang.name}</span>
                  </Link>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </nav>

      <AnimatePresence>
        {mobileMenuOpen && (
          <MobileNavbar
            navigation={navigation}
            setMobileMenuOpen={setMobileMenuOpen}
            locale={locale}
            pathname={pathname || ''}
            dictionary={dictionary}
          />
        )}
      </AnimatePresence>
    </header>
  )
}
